import { Button, Card, Modal, Stack, Typography } from '@prometheus/components'
import React, { createContext, useContext, useState, ReactNode } from 'react'

export interface ErrorInfo {
  error: Error
  errorInfo: React.ErrorInfo
  componentName: string
  timestamp: Date
}

interface ErrorBoundaryContextType {
  errors: ErrorInfo[]
  addError: (error: Error, errorInfo: React.ErrorInfo, componentName: string) => void
  clearError: (index: number) => void
  clearAllErrors: () => void
  showModal: boolean
  setShowModal: (show: boolean) => void
}

const ErrorBoundaryContext = createContext<ErrorBoundaryContextType | undefined>(undefined)

export const useErrorBoundary = () => {
  const context = useContext(ErrorBoundaryContext)
  if (!context) {
    throw new Error('useErrorBoundary must be used within an ErrorBoundaryProvider')
  }
  return context
}

interface ErrorBoundaryProviderProps {
  children: ReactNode
}

export const ErrorBoundaryProvider: React.FC<ErrorBoundaryProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorInfo[]>([])
  const [showModal, setShowModal] = useState(false)

  const addError = (error: Error, errorInfo: React.ErrorInfo, componentName: string) => {
    const newError: ErrorInfo = {
      error,
      errorInfo,
      componentName,
      timestamp: new Date()
    }
    
    setErrors(prev => [...prev, newError])
    setShowModal(true)
  }

  const clearError = (index: number) => {
    setErrors(prev => prev.filter((_, i) => i !== index))
  }

  const clearAllErrors = () => {
    setErrors([])
    setShowModal(false)
  }

  const value: ErrorBoundaryContextType = {
    errors,
    addError,
    clearError,
    clearAllErrors,
    showModal,
    setShowModal
  }

  return (
    <ErrorBoundaryContext.Provider value={value}>
      {children}
      {showModal && <ErrorModal />}
    </ErrorBoundaryContext.Provider>
  )
}

// Mock Error Modal Component - you can replace this with your own design
const ErrorModal: React.FC = () => {
  const { errors, clearAllErrors, setShowModal } = useErrorBoundary()
  const latestError = errors[errors.length - 1]

  if (!latestError) return null

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleDismiss = () => {
    setShowModal(false)
  }

  return (
    <Modal onClose={() => {}}>
      <Card borderColor='danger' padding="sm">
        <Stack>
          <Typography variant="h3" retro color="danger">Critical Error {'=('}</Typography>
          <Typography>Something went wrong in <Typography component="span" color="danger" retro>{latestError.componentName}.tsx</Typography>!</Typography>
          <Card style={{ maxWidth: '400px', maxHeight: '200px', overflow: 'auto' }} borderColor='danger' padding="sm" variant="elevated">
            <Typography style={{ fontFamily: 'var(--prometheus-font-family-mono', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{latestError.error.message}{'\n'}{latestError.error.stack ?? ''}</Typography>
          </Card>
          <Button variant="danger" outline onClick={handleRefresh}>Reload Page</Button>
        </Stack>
      </Card>
    </Modal>
  )
}